import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ExternalLink } from 'lucide-react';
import { useLang } from '../context/LanguageContext';
import { useEvent } from '../context/EventContext';
import { LanguageSelector } from '../components/LanguageSelector';

export default function Welcome() {
  const { t } = useLang();
  const { config } = useEvent();
  const navigate = useNavigate();
  const brand = config?.brand?.name || 'Champagne Pommery';
  const org = config?.organizer?.name || 'Business Club France-Latvia';
  const event = config?.event || {};
  const brandLogo = config?.brand?.logo_url;
  const venueLogo = event.venue_logo_url;
  const platformLogo = config?.platform?.logo_url;
  const heroVideo = event.hero_video_url;
  const heroPoster = event.hero_image_url;
  const contactUrl = config?.settings?.contact_cta_url;
  const contactLabel = t('contactCta');

  return (
    <div className="app-frame min-h-screen flex flex-col" data-testid="welcome-screen">
      <div className="relative aspect-[3/4] overflow-hidden border-b border-[#D4AF37]/20 bg-[#070C1E]">
        {heroVideo ? (
          <video
            data-testid="hero-video"
            autoPlay
            muted
            loop
            playsInline
            preload="metadata"
            poster={heroPoster || undefined}
            className="absolute inset-0 w-full h-full object-cover object-center"
          >
            <source src={heroVideo} type="video/mp4" />
          </video>
        ) : (
          <img
            src={heroPoster}
            alt="Champagne Pommery"
            loading="eager"
            className="absolute inset-0 w-full h-full object-cover object-center"
          />
        )}
        {/* dark overlay for text readability */}
        <div className="absolute inset-0 bg-gradient-to-b from-[#070C1E]/45 via-[#070C1E]/70 to-[#070C1E]" />
        <div className="absolute inset-0 flex flex-col items-center justify-end pb-8 px-6 text-center">
          {brandLogo ? (
            <img src={brandLogo} alt={brand} className="h-28 w-auto object-contain drop-shadow-[0_2px_14px_rgba(0,0,0,0.55)] mb-4" data-testid="brand-logo" />
          ) : (
            <h1 className="font-serif font-bold text-4xl leading-tight text-[#FDFBF7]">{brand}</h1>
          )}
          <span className="font-serif text-2xl text-[#D4AF37] my-1">×</span>
          <h2 className="font-serif text-xl text-[#FDFBF7]/90">{org}</h2>
        </div>
      </div>

      <div className="flex-1 flex flex-col items-center px-6 pt-6 pb-8 text-center">
        <p className="font-sans text-base text-slate-300 mb-1">{t('experienceTitle')}</p>
        <p className="font-serif text-lg text-[#E5C158]">{event.date_label}</p>
        <p className="font-sans text-sm text-slate-400 mb-4">{event.venue}</p>
        {venueLogo && (
          <img src={venueLogo} alt={event.venue} className="h-10 w-auto object-contain brightness-0 invert opacity-90 mb-6" data-testid="venue-logo" />
        )}

        <button
          data-testid="start-experience-button"
          onClick={() => navigate('/taste')}
          className="w-full bg-gradient-to-r from-[#D4AF37] to-[#E5C158] text-[#070C1E] font-bold py-4 rounded-xl text-base shadow-lg shadow-[#D4AF37]/20 active:scale-[0.98] transition-all mb-8"
        >
          {t('startExperience')}
        </button>

        <p className="font-mono text-[10px] tracking-widest uppercase text-slate-500 mb-3">{t('chooseLanguage')}</p>
        <LanguageSelector variant="welcome" />

        <div className="mt-auto pt-10 flex flex-col items-center gap-4">
          {contactUrl && (
            <a
              href={contactUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-cta"
              className="w-full flex items-center justify-center gap-2 border border-slate-400/40 text-slate-300 font-medium py-4 px-6 rounded-xl text-[13px] tracking-wide hover:border-slate-300 hover:text-slate-200 transition-all"
            >
              {contactLabel} <ExternalLink size={14} className="shrink-0" />
            </a>
          )}
          {platformLogo && (
            <img src={platformLogo} alt="0g Baltics" className="h-10 w-auto object-contain opacity-90" data-testid="platform-logo" />
          )}
          <p className="font-sans text-[11px] text-slate-500">{config?.platform?.footer_attribution || t('createdBy')}</p>
        </div>
      </div>
    </div>
  );
}
